import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import TaskIcon from '@mui/icons-material/Task';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import QueueIcon from '@mui/icons-material/Queue';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from '../components/toast';
import { getBackgroundTasks, getBackgroundTasksStats, getBackgroundTaskOutputFileUrl } from '../services/api';
import Scrollbar from '../components/scrollbar';
import SearchBar from '../sections/@dashboard/SearchBar';
import StatsCards from '../components/common/statsCard';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import Iconify from '../components/iconify';

export default function BackgroundTasks() {
  const [backgroundTasks, setBackgroundTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState('');
  const [stats, setStats] = useState({
    total_tasks: 0,
    completed_tasks: 0,
    failed_tasks: 0,
    queued_tasks: 0,
  });

  const getTasks = useCallback(
    async (currentPage = page, currentRowsPerPage = rowsPerPage) => {
      setLoading(true);
      try {
        const response = await getBackgroundTasks(currentPage + 1, currentRowsPerPage);
        setBackgroundTasks(response.data.results);
        setTotalCount(response.data.count);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        toast.error('Unable to get Background tasks, try again later.');
        setLoading(false);
      }
    },
    [page, rowsPerPage]
  );

  const getStats = useCallback(async () => {
    try {
      const response = await getBackgroundTasksStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getTasks(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    getTasks(0, newRowsPerPage);
  };

  const handleOpen = (remarks) => {
    setSelectedRemarks(remarks);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedRemarks('');
  };

  const handleRefresh = async () => {
    await Promise.all([getTasks(page, rowsPerPage), getStats()]);
  };

  const handleSearch = () => {};

  const handleDownload = async (taskId) => {
    try {
      const response = await getBackgroundTaskOutputFileUrl(taskId);
      const url = response.data.download_url;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading output file:', error);
      toast.error('Unable to download the output file, try again later.');
    }
  };

  useEffect(() => {
    getTasks(0, rowsPerPage);
    getStats();
  }, [getTasks, getStats, rowsPerPage]);

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Tasks</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Tasks
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Monitor long-running processes, data exports, and system maintenance tasks.
              </Typography>
            </Box>
            <LightTooltip title="Refresh the task list and statistics.">
              <Button
                variant="contained"
                size="medium"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  backgroundColor: '#0A9EF2',
                  boxShadow: () => alpha('#0A9EF2', 0.24),
                  '&:hover': {
                    backgroundColor: '#0886cc',
                    boxShadow: 'none',
                  },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Refresh Tasks
              </Button>
            </LightTooltip>
          </Stack>

          <StatsCards
            stats={[
              {
                title: 'Total Tasks',
                value: stats.total_tasks || 0,
                icon: <TaskIcon color="primary" />,
              },
              {
                title: 'Completed',
                value: stats.completed_tasks || 0,
                icon: <DoneOutlineIcon color="primary" />,
              },
              {
                title: 'Failed',
                value: stats.failed_tasks || 0,
                icon: <CancelIcon color="primary" />,
              },
              {
                title: 'Queued',
                value: stats.queued_tasks || 0,
                icon: <QueueIcon color="primary" />,
              },
            ]}
          />

          <Box paddingY={2}>
            <SearchBar onSearch={handleSearch} />
          </Box>
          <Card sx={{ borderRadius: 2, boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Task Name</TableCell>
                      <TableCell>Task Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Remarks</TableCell>
                      <TableCell>Output File</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Time Taken</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell align="center" colSpan={8} sx={{ py: 8 }}>
                          <CircularProgress />
                          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            Loading background tasks...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : backgroundTasks.length === 0 ? (
                      <TableRow>
                        <TableCell align="center" colSpan={8} sx={{ py: 10 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Iconify
                              icon="eva:list-outline"
                              sx={{ mb: 2, width: 48, height: 48, color: 'text.disabled' }}
                            />
                            <Typography variant="h6" gutterBottom>
                              No Tasks Found
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              There are currently no background tasks to display.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      backgroundTasks.map((task) => (
                        <TableRow
                          key={task.id}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="subtitle2">{task.id}</Typography>
                          </TableCell>
                          <TableCell>{task.task_name}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {task.task_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={task.status}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                backgroundColor: (theme) => {
                                  switch (task.status) {
                                    case 'Failed':
                                      return alpha(theme.palette.error.main, 0.12);
                                    case 'Queued':
                                      return alpha(theme.palette.info.main, 0.12);
                                    case 'Completed':
                                      return alpha(theme.palette.success.main, 0.12);
                                    default:
                                      return theme.palette.action.selected;
                                  }
                                },
                                color: (theme) => {
                                  switch (task.status) {
                                    case 'Failed':
                                      return theme.palette.error.main;
                                    case 'Queued':
                                      return theme.palette.info.main;
                                    case 'Completed':
                                      return theme.palette.success.main;
                                    default:
                                      return theme.palette.text.primary;
                                  }
                                },
                                border: (theme) => {
                                  let color;
                                  switch (task.status) {
                                    case 'Failed':
                                      color = theme.palette.error.main;
                                      break;
                                    case 'Queued':
                                      color = theme.palette.info.main;
                                      break;
                                    case 'Completed':
                                      color = theme.palette.success.main;
                                      break;
                                    default:
                                      color = theme.palette.divider;
                                  }
                                  return `1px solid ${alpha(color, 0.24)}`;
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {task.remarks ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                  {task.remarks.split('\n')[0]}
                                </Typography>
                                <LightTooltip title="View full remarks">
                                  <IconButton size="small" onClick={() => handleOpen(task.remarks)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </LightTooltip>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {task.output_file_key ? (
                              <LightTooltip title="Download output file">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownload(task.id)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </LightTooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">N/A</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {dayjs(task.created_at).format('MMM DD, YYYY')}
                              <br />
                              {dayjs(task.created_at).format('hh:mm A')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {task.time_taken_secs ? `${task.time_taken_secs}s` : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 15, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Grid>
      </Grid>
      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 2 }}>Task Remarks</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', py: 1 }}>
            {selectedRemarks?.replaceAll('\\n', '\n')}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
